import React from 'react';
import { X, Clock, CheckCircle, XCircle, Loader2, FileText } from 'lucide-react';
import { HistoryItem } from './HistoryPanel';

type DrawerSize = '2xl' | '3xl' | '4xl' | '5xl' | 'vw60' | 'vw70' | 'vw80';

const sizeClass: Record<DrawerSize, string> = {
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
  '5xl': 'max-w-5xl',
  'vw60': 'w-[60vw] min-w-[720px]',
  'vw70': 'w-[70vw] min-w-[800px]',
  'vw80': 'w-[80vw] min-w-[900px]',
};

interface TransactionDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  item: HistoryItem | null;
  renderDetails?: (item: HistoryItem) => React.ReactNode;
  size?: DrawerSize;
  title?: string;
  hideMetadata?: boolean;
}

const TransactionDrawer: React.FC<TransactionDrawerProps> = ({
  isOpen,
  onClose,
  item,
  renderDetails,
  size = '2xl',
  hideMetadata = false,
  title,
}) => {
  if (!isOpen || !item) return null;

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'processing':
        return <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />;
      case 'pending':
        return <Clock className="h-5 w-5 text-yellow-500" />;
      case 'failed':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <FileText className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'processing':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'failed':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40 transition-opacity duration-300"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className={`fixed right-0 top-0 h-full ${sizeClass[size]} bg-white dark:bg-gray-800 shadow-2xl z-50 transform transition-transform duration-300 ease-in-out overflow-hidden flex flex-col`}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div className="flex items-center space-x-3">
            {getStatusIcon(item.status)}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {title || 'Transaction Details'}
              </h2>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${getStatusColor(item.status)}`}>
                {item.status}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        {hideMetadata && renderDetails ? (
          // Full-height renderDetails — skip the core info grid, fill all remaining space
          <div className="flex-1 min-h-0 overflow-hidden flex flex-col p-4">
            {renderDetails(item)}
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Core info grid */}
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Title
                </label>
                <p className="mt-1 text-base font-medium text-gray-900 dark:text-white">
                  {item.title}
                </p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Type
                </label>
                <p className="mt-1 text-sm text-gray-700 dark:text-gray-300 capitalize">
                  {item.type.replace(/-/g, ' ')}
                </p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Created At
                </label>
                <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">
                  {formatDate(item.createdAt)}
                </p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Transaction ID
                </label>
                <p className="mt-1 text-sm font-mono text-gray-700 dark:text-gray-300 break-all">
                  {item.id}
                </p>
              </div>
            </div>

            <hr className="border-gray-200 dark:border-gray-700" />

            {/* Custom Details */}
            {renderDetails && renderDetails(item)}

            {/* Metadata */}
            {!hideMetadata && item.metadata && Object.keys(item.metadata).filter(k => item.metadata![k] !== undefined).length > 0 && (
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Additional Information
              </label>
              <div className="mt-3 grid grid-cols-2 gap-4">
                {Object.entries(item.metadata)
                  .filter(([, value]) => value !== undefined && value !== null)
                  .map(([key, value]) => (
                    <div key={key} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 capitalize mb-1">
                        {key.replace(/([A-Z])/g, ' $1').trim()}
                      </p>
                      <p className="text-sm text-gray-900 dark:text-white break-words">
                        {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                      </p>
                    </div>
                    ))}
              </div>
            </div>
          )}
        </div>
        )}

        {/* Footer — only shown in standard (non full-height) mode */}
        {!(hideMetadata && renderDetails) && (
          <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
            <button
              onClick={onClose}
              className="w-full px-4 py-2.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 
                       rounded-lg font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default TransactionDrawer;
